import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { COMPANY_SEARCH_CLIENT } from "../../../../../utils/consts";
import { SPEND_BY_EMPLOYEES_RANGE_QUERY } from "../../../../../legacy/queries/MI";
import { CustomEmployeeRange } from "../../../../../legacy/types/companySearch/MI";
import { sanitizeCompanyFilters } from "../../../../../legacy/utils/companyFilterUtils";
import { CompanyFilter } from "../../../../../legacy/types/companyFilter";
import { useWorkspaceStore } from "../../../../../store/workspace/WorkspaceStore";
import { MinMaxValue } from "../../../../../types/filters";
import type {
  ModelScoreInput,
  ModelScoreFilterInput,
} from "../../../../../generated/company-search";

const getMinMaxRangeValues = (
  employeeRanges: CustomEmployeeRange[]
): MinMaxValue[] =>
  employeeRanges.map((range) => ({
    min: range.min || Number.NEGATIVE_INFINITY,
    max: range.max || Number.POSITIVE_INFINITY,
  }));

type EmployeeBreakdownQueryPayload = {
  segmentAnalysisEmployeeRangeBreakdown: {
    companyCount: number;
    totalSpend: number;
    range: MinMaxValue;
  }[];
};

interface Props {
  companyFilters: CompanyFilter;
  customSpendCategory?: {
    name: string;
    categories: Array<{ id: string; weight: number }>;
  };
  employeeRanges: CustomEmployeeRange[];
  localizeInstalls?: boolean;
  /** V2 ("model tier") scoring narrowing — mirrors opportunityGeneratorSearch's
   * args. */
  tenantId?: string;
  modelScores?: ModelScoreInput[];
  modelScoreFilter?: ModelScoreFilterInput[];
  /** Share-page panel tag (Apollo context); see toPanelDescriptor. */
  activeModelId?: string;
  /** Skips the query entirely. Omitted (undefined) behaves exactly like
   * `false` — existing callers that never pass this see no change. */
  skip?: boolean;
}

export function useGetSpendByEmployeeRangeData(props: Props) {
  const workspaceWhitespace = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );

  const {
    loading: isLoading,
    data,
    error,
  } = useQuery<EmployeeBreakdownQueryPayload>(SPEND_BY_EMPLOYEES_RANGE_QUERY, {
    context: {
      clientName: COMPANY_SEARCH_CLIENT,
      activeModelId: props.activeModelId,
    },
    skip: props.skip,
    variables: {
      opportunityGeneratorFilters: sanitizeCompanyFilters(
        props.companyFilters,
        true
      ),
      localizeInstalls: props.localizeInstalls,
      customSpendCategory: props.customSpendCategory,
      ranges:
        props.employeeRanges.length === 0
          ? null
          : getMinMaxRangeValues(props.employeeRanges),
      companyMatchFile:
        props.companyFilters?.opportunityType &&
        workspaceWhitespace?.fileId &&
        workspaceWhitespace?.lastMatched
          ? workspaceWhitespace
          : undefined,
      tenantId: props.tenantId,
      modelScores: props.modelScores,
      modelScoreFilter: props.modelScoreFilter,
    },
  });

  const spendBreakdownResults = useMemo(() => {
    return data?.segmentAnalysisEmployeeRangeBreakdown
      ?.map((result) => {
        return {
          companies: result.companyCount,
          spend: result.totalSpend,
          dimension:
            props.employeeRanges.find(({ max, min }) =>
              [min, max].some((minMaxValue) =>
                [result.range.max, result.range.min].includes(
                  minMaxValue ||
                    Number.POSITIVE_INFINITY ||
                    Number.NEGATIVE_INFINITY
                )
              )
            )?.label || "unknown",
        };
      })
      .reverse();
  }, [props.employeeRanges, data]);

  return {
    isLoading,
    error,
    spendBreakdownResults: spendBreakdownResults || [],
  };
}
